import { useEffect, useState } from 'react';

interface Article {
  title: string;
  readTime: string;
  description: string;
  imageUrl?: string;
  link: string;
}

interface FontSizeResponsive {
  desktop?: number;
  tablet?: number;
  mobile?: number;
}

interface Props {
  articles?: Article[];
  itemsPerPage?: number;
  fontFamily?: string;

  titleFontFamily?: string;
  titleFontSize?: FontSizeResponsive; // changed from number to object
  titleFontWeight?: string;
  titleFontStyle?: 'normal' | 'italic';
  titleColor?: string;

  readTimeFontSize?: FontSizeResponsive;
  readTimeColor?: string;
  readTimeFontFamily?: string;

  descriptionFontFamily?: string;
  descriptionFontSize?: FontSizeResponsive;
  descriptionFontWeight?: string;
  descriptionFontStyle?: 'normal' | 'italic';
  descriptionColor?: string;

  linkFontFamily?: string;
  linkFontSize?: FontSizeResponsive;
  linkFontWeight?: string;
  linkFontStyle?: 'normal' | 'italic';
  linkColor?: string;
}

export function ArticleCardGrid({
  articles = [],
  itemsPerPage = 9,
  fontFamily = 'GT-Walsheim-Pro-Regular',

  titleFontFamily,
  titleFontSize = { desktop: 24, tablet: 20, mobile: 18 },
  titleFontWeight = 'bold',
  titleFontStyle = 'normal',
  titleColor = '#000',

  readTimeFontSize = { desktop: 14, tablet: 12, mobile: 10 },
  readTimeColor = '#888',
  readTimeFontFamily,

  descriptionFontFamily,
  descriptionFontSize = { desktop: 14, tablet: 12, mobile: 10 },
  descriptionFontWeight = 'normal',
  descriptionFontStyle = 'normal',
  descriptionColor = '#444',

  linkFontFamily,
  linkFontSize = { desktop: 16, tablet: 14, mobile: 12 },
  linkFontWeight = 'bold',
  linkFontStyle = 'normal',
  linkColor = '#63adf0',
}: Props) {
  const [visibleCount, setVisibleCount] = useState(itemsPerPage);
  const visibleArticles = articles.slice(0, visibleCount);
  const canLoadMore = articles.length > visibleCount;

  // Helper to create CSS variable styles for font sizes in px
  // We'll inject CSS variables for the root container and use those in styles with media queries
  const rootId = 'article-card-grid-root';

  return (
    <>
      <div
        id={rootId}
        style={{
          fontFamily,
          padding: '2rem',
        }}
      >
        {/* 3-column grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '2rem',
          }}
        >
          {visibleArticles.map((article, index) => (
            <div
              key={index}
              style={{
                border: '1px solid #eee',
                borderRadius: '8px',
                padding: '1rem',
                backgroundColor: '#fff',
                boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              {article.imageUrl && (
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '4px',
                    marginBottom: '3rem',
                  }}
                />
              )}
              <div
                className='article-title'
                style={{
                  fontWeight: titleFontWeight,
                  fontStyle: titleFontStyle,
                  fontFamily: titleFontFamily || fontFamily,
                  color: titleColor,
                  marginBottom: '0.25rem',
                  fontSize: `var(--title-font-size)`,
                }}
              >
                {article.title}
              </div>

              <div
                className='article-readTime'
                style={{
                  color: readTimeColor,
                  fontFamily: readTimeFontFamily || fontFamily,
                  marginBottom: '1.75rem',
                  fontSize: `var(--readTime-font-size)`,
                }}
              >
                {article.readTime}
              </div>

              <div
                className='article-description'
                style={{
                  color: descriptionColor,
                  fontWeight: descriptionFontWeight,
                  fontStyle: descriptionFontStyle,
                  fontFamily: descriptionFontFamily || fontFamily,
                  flexGrow: 1,
                  fontSize: `var(--description-font-size)`,
                }}
              >
                {article.description}
              </div>

              <a
                href={article.link}
                target='_blank'
                rel='noopener noreferrer'
                style={{
                  color: linkColor,
                  fontWeight: linkFontWeight,
                  fontStyle: linkFontStyle,
                  fontFamily: linkFontFamily || fontFamily,
                  marginTop: '2rem',
                  marginBottom: '4rem',
                  textDecoration: 'none',
                  fontSize: `var(--link-font-size)`,
                }}
              >
                Read More...
              </a>
            </div>
          ))}
        </div>

        {canLoadMore && (
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <button
              onClick={() => setVisibleCount((prev) => prev + itemsPerPage)}
              style={{
                padding: '0.75rem 2rem',
                borderRadius: '6px',
                border: '1px solid #00b2ff',
                backgroundColor: '#fff',
                color: '#0082ca',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
              }}
            >
              LOAD MORE
            </button>
          </div>
        )}
      </div>

      <style>{`
        #${rootId} {
          --title-font-size: ${titleFontSize.desktop}px;
          --readTime-font-size: ${readTimeFontSize.desktop}px;
          --description-font-size: ${descriptionFontSize.desktop}px;
          --link-font-size: ${linkFontSize.desktop}px;
        }
        @media (max-width: 1024px) {
          #${rootId} {
            --title-font-size: ${titleFontSize.tablet ?? titleFontSize.desktop}px;
            --readTime-font-size: ${readTimeFontSize.tablet ?? readTimeFontSize.desktop}px;
            --description-font-size: ${descriptionFontSize.tablet ?? descriptionFontSize.desktop}px;
            --link-font-size: ${linkFontSize.tablet ?? linkFontSize.desktop}px;
          }
        }
        @media (max-width: 600px) {
          #${rootId} {
            --title-font-size: ${titleFontSize.mobile ?? titleFontSize.desktop}px;
            --readTime-font-size: ${readTimeFontSize.mobile ?? readTimeFontSize.desktop}px;
            --description-font-size: ${descriptionFontSize.mobile ?? descriptionFontSize.desktop}px;
            --link-font-size: ${linkFontSize.mobile ?? linkFontSize.desktop}px;
          }
        }
        @media (max-width: 1024px) {
          div[style*="grid-template-columns"] {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 600px) {
          div[style*="grid-template-columns"] {
            grid-template-columns: repeat(1, 1fr) !important;
          }
        }
      `}</style>
    </>
  );
}
